import { Component } from '@theme/component';

/**
 * <ms-ugc-component> — Control de la grid/carrusel UGC (sections/ms-ugc-grid.liquid).
 *
 * Responsabilidades (todas opcionales según markup presente; nada es requerido salvo
 * el propio elemento, así degrada con elegancia si falta cualquier pieza):
 *
 *   1. Reproducción perezosa de video en viewport
 *      - Solo videos Shopify nativos (<video data-ms-ugc-video>): autoplay mute
 *        cuando entran al viewport y se pausan al salir (IntersectionObserver),
 *        respetando prefers-reduced-motion (si reduce → NO autoplay).
 *      - Botón play/pause por tile (ref="videoToggle[]") para control manual a11y.
 *
 *   2. Carrusel (mobile/desktop): riel scroll-snap + dots + flechas.
 *      Reutiliza el mismo enfoque medido por scrollWidth/clientWidth que ms-related.
 *
 *   3. Lightbox/reel: al activar un tile (ref="lightboxTrigger[]") abre un overlay
 *      modal accesible (focus trap, Esc, click fuera) que clona la media ampliada.
 *
 * Refs:
 *   track             - riel scrolleable (solo en modo carrusel). Opcional.
 *   dot[]             - dots de paginación del carrusel. Opcional.
 *   prev / next       - flechas del carrusel. Opcional.
 *   videoToggle[]     - botón play/pausa por video. Opcional.
 *   lightboxTrigger[] - botón/elemento que abre el lightbox para un tile. Opcional.
 *   lightbox          - <dialog> contenedor del lightbox. Opcional.
 *   lightboxStage     - nodo donde se inyecta la media ampliada. Opcional.
 *   lightboxClose     - botón de cierre del lightbox. Opcional.
 *
 * Atributos:
 *   data-loop="true"          - el carrusel salta del último al primero y viceversa.
 *   data-autoplay="true"      - habilita autoplay de video en viewport.
 *
 * @extends {Component}
 */
class MsUgcComponent extends Component {
  // El elemento es autosuficiente: no exigimos refs (la sección puede estar en
  // modo grid sin carrusel, sin video y sin lightbox).
  requiredRefs = [];

  #abortController = new AbortController();

  /** @type {IntersectionObserver | undefined} */
  #videoObserver;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  /** @type {number} Página activa del carrusel (índice 0). */
  #activeIndex = 0;

  /** @type {number} Total de páginas del carrusel. */
  #pageCount = 1;

  /** @type {number | undefined} */
  #scrollRaf;

  /** @type {HTMLElement | null} Elemento con foco antes de abrir el lightbox. */
  #lastFocused = null;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    this.#reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.#setupVideos(signal);
    this.#setupCarousel(signal);
    this.#setupLightbox(signal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#videoObserver?.disconnect();
    this.#resizeObserver?.disconnect();
    if (this.#scrollRaf) cancelAnimationFrame(this.#scrollRaf);
    // Restaurar el scroll del body si quedó bloqueado por el lightbox.
    document.documentElement.style.removeProperty('overflow');
  }

  /** Re-sincroniza tras un re-render del Section Rendering API. */
  updatedCallback() {
    super.updatedCallback?.();
    this.#measure();
  }

  /** @type {boolean} */
  #reduceMotion = false;

  /** @type {boolean} Preferencia de sonido del visitante para esta sección (default: silencio). */
  #soundEnabled = false;

  /* ========================================================================
     1) VIDEO: autoplay perezoso + toggle manual
     ===================================================================== */

  /** @param {AbortSignal} signal */
  #setupVideos(signal) {
    /** @type {HTMLVideoElement[]} */
    const videos = Array.from(this.querySelectorAll('video[data-ms-ugc-video]'));
    if (videos.length === 0) return;

    const autoplay = this.dataset.autoplay === 'true' && !this.#reduceMotion;

    // Botón play/pausa por tile: alterna y refleja estado.
    this.addEventListener(
      'click',
      (event) => {
        const btn = /** @type {HTMLElement} */ (event.target)?.closest('[data-ms-ugc-video-toggle]');
        if (!btn) return;
        const tile = btn.closest('[data-ms-ugc-tile]');
        const video = tile?.querySelector('video[data-ms-ugc-video]');
        if (!(video instanceof HTMLVideoElement)) return;
        event.preventDefault();
        if (video.paused) {
          this.#playVideo(video, this.#soundEnabled);
        } else {
          video.pause();
        }
      },
      { signal }
    );

    // Botón de altavoz (mute/unmute) por tile. Garantiza un solo audio a la vez.
    this.addEventListener(
      'click',
      (event) => {
        const btn = /** @type {HTMLElement} */ (event.target)?.closest('[data-ms-ugc-sound-toggle]');
        if (!btn) return;
        const tile = btn.closest('[data-ms-ugc-tile]');
        const video = tile?.querySelector('video[data-ms-ugc-video]');
        if (!(video instanceof HTMLVideoElement)) return;
        event.preventDefault();
        this.#soundEnabled = !this.#soundEnabled;
        // Un solo audio: silenciar el resto.
        for (const other of videos) {
          if (other !== video) other.muted = true;
        }
        video.muted = !this.#soundEnabled;
        // Activar sonido es un gesto del usuario → si está pausado, reproducir con sonido.
        if (this.#soundEnabled && video.paused) this.#playVideo(video, true);
        this.#syncSoundButtons(videos);
      },
      { signal }
    );

    // Reflejar estado real del video en los botones (icono/aria).
    for (const video of videos) {
      video.addEventListener('play', () => this.#syncVideoToggle(video, true), { signal });
      video.addEventListener('pause', () => this.#syncVideoToggle(video, false), { signal });
      // Mantener el botón de altavoz en sincronía si cambia el mute (controles nativos, etc.).
      video.addEventListener('volumechange', () => this.#syncSoundButtons(videos), { signal });
    }

    // Estado inicial de los botones de altavoz.
    this.#syncSoundButtons(videos);

    if (autoplay && 'IntersectionObserver' in window) {
      this.#videoObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const video = /** @type {HTMLVideoElement} */ (entry.target);
            if (entry.isIntersecting) {
              this.#playVideo(video);
            } else {
              video.pause();
            }
          }
        },
        { threshold: 0.5 }
      );
      for (const video of videos) this.#videoObserver.observe(video);
    }
  }

  /**
   * @param {HTMLVideoElement} video
   * @param {boolean} [allowSound=false] true = reproducir con sonido. Solo válido tras
   *   un gesto del usuario (tap en play, altavoz o lightbox). El autoplay en viewport
   *   SIEMPRE pasa false: la política de autoplay de los navegadores exige mute.
   */
  #playVideo(video, allowSound = false) {
    // Sin sonido el autoplay programático siempre se permite; con sonido solo si
    // viene de un gesto del usuario.
    video.muted = !allowSound;
    const promise = video.play();
    if (promise && typeof promise.catch === 'function') {
      promise.catch(() => {
        /* Autoplay bloqueado: el usuario puede pulsar play manualmente. */
      });
    }
  }

  /**
   * @param {HTMLVideoElement} video
   * @param {boolean} playing
   */
  #syncVideoToggle(video, playing) {
    const tile = video.closest('[data-ms-ugc-tile]');
    const btn = tile?.querySelector('[data-ms-ugc-video-toggle]');
    if (!(btn instanceof HTMLElement)) return;
    btn.setAttribute('aria-pressed', playing ? 'true' : 'false');
    btn.dataset.playing = playing ? 'true' : 'false';
  }

  /**
   * Refleja el estado de mute de cada video en su botón de altavoz.
   * @param {HTMLVideoElement[]} videos
   */
  #syncSoundButtons(videos) {
    for (const video of videos) {
      const tile = video.closest('[data-ms-ugc-tile]');
      const btn = tile?.querySelector('[data-ms-ugc-sound-toggle]');
      if (!(btn instanceof HTMLElement)) continue;
      const muted = video.muted;
      btn.dataset.muted = muted ? 'true' : 'false';
      // aria-pressed=true cuando el sonido está activo (no muted).
      btn.setAttribute('aria-pressed', muted ? 'false' : 'true');
    }
  }

  /* ========================================================================
     2) CARRUSEL: dots + flechas + scroll-snap medido
     ===================================================================== */

  /** @param {AbortSignal} signal */
  #setupCarousel(signal) {
    const { track } = this.refs;
    if (!(track instanceof HTMLElement)) return;

    track.addEventListener('scroll', this.#onScroll, { passive: true, signal });

    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(() => this.#measure());
      this.#resizeObserver.observe(track);
    } else {
      window.addEventListener('resize', this.#measure, { signal });
    }

    this.#measure();
  }

  #onScroll = () => {
    if (this.#scrollRaf) cancelAnimationFrame(this.#scrollRaf);
    this.#scrollRaf = requestAnimationFrame(() => {
      this.#syncActiveFromScroll();
      this.#renderControls();
    });
  };

  #measure = () => {
    const { track } = this.refs;
    if (!(track instanceof HTMLElement)) return;

    const visible = track.clientWidth;
    const total = track.scrollWidth;
    const pages = visible > 0 ? Math.max(1, Math.round(total / visible)) : 1;
    this.#pageCount = pages;

    this.toggleAttribute('data-no-overflow', pages <= 1);

    this.#syncActiveFromScroll();
    this.#renderControls();
  };

  #syncActiveFromScroll() {
    const { track } = this.refs;
    if (!(track instanceof HTMLElement)) return;
    const visible = track.clientWidth;
    if (visible <= 0) return;

    const max = track.scrollWidth - visible;
    const left = track.scrollLeft;
    let index = Math.round(left / visible);
    if (left >= max - 1) index = this.#pageCount - 1;
    this.#activeIndex = Math.min(Math.max(index, 0), this.#pageCount - 1);
  }

  #renderControls() {
    const dots = this.#dots;
    dots.forEach((dot, i) => {
      const within = i < this.#pageCount;
      dot.hidden = !within;
      if (!within) {
        dot.setAttribute('aria-current', 'false');
        dot.classList.remove('is-active');
        dot.tabIndex = -1;
        return;
      }
      const isActive = i === this.#activeIndex;
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
      dot.classList.toggle('is-active', isActive);
      dot.tabIndex = isActive ? 0 : -1;
    });

    const loop = this.dataset.loop === 'true';
    const { prev, next } = this.refs;
    if (prev instanceof HTMLButtonElement) prev.disabled = !loop && this.#activeIndex <= 0;
    if (next instanceof HTMLButtonElement) next.disabled = !loop && this.#activeIndex >= this.#pageCount - 1;
  }

  /** @returns {HTMLButtonElement[]} */
  get #dots() {
    const { dot } = this.refs;
    if (!dot) return [];
    return /** @type {HTMLButtonElement[]} */ (Array.isArray(dot) ? dot : [dot]);
  }

  /**
   * Ir a una página (dot). on:click="/goTo?index=N".
   * @param {{ index: number | string }} data
   */
  goTo(data) {
    const index = typeof data === 'object' ? Number(data.index) : Number(data);
    if (Number.isNaN(index)) return;
    this.#scrollToPage(index);
  }

  /** Página anterior (flecha). on:click="/prevPage". */
  prevPage() {
    let target = this.#activeIndex - 1;
    if (target < 0) target = this.dataset.loop === 'true' ? this.#pageCount - 1 : 0;
    this.#scrollToPage(target);
  }

  /** Página siguiente (flecha). on:click="/nextPage". */
  nextPage() {
    let target = this.#activeIndex + 1;
    if (target > this.#pageCount - 1) target = this.dataset.loop === 'true' ? 0 : this.#pageCount - 1;
    this.#scrollToPage(target);
  }

  /**
   * Teclado en la tira de dots: flechas izq/der mueven foco + página.
   * on:keydown="/onDotKeydown".
   * @param {KeyboardEvent} event
   */
  onDotKeydown(event) {
    const key = event.key;
    if (key !== 'ArrowLeft' && key !== 'ArrowRight') return;
    event.preventDefault();
    const delta = key === 'ArrowRight' ? 1 : -1;
    let target = this.#activeIndex + delta;
    target = Math.min(Math.max(target, 0), this.#pageCount - 1);
    this.#scrollToPage(target);
    this.#dots[target]?.focus();
  }

  /** @param {number} index */
  #scrollToPage(index) {
    const { track } = this.refs;
    if (!(track instanceof HTMLElement)) return;
    const clamped = Math.min(Math.max(index, 0), this.#pageCount - 1);
    const left = track.clientWidth * clamped;
    track.scrollTo({ left, behavior: this.#reduceMotion ? 'auto' : 'smooth' });
    this.#activeIndex = clamped;
    this.#renderControls();
  }

  /* ========================================================================
     3) LIGHTBOX / REEL accesible
     ===================================================================== */

  /** @param {AbortSignal} signal */
  #setupLightbox(signal) {
    const { lightbox } = this.refs;
    if (!(lightbox instanceof HTMLElement)) return;

    // Abrir: cualquier disparador con data-ms-ugc-lightbox-trigger.
    this.addEventListener(
      'click',
      (event) => {
        const trigger = /** @type {HTMLElement} */ (event.target)?.closest('[data-ms-ugc-lightbox-trigger]');
        if (!trigger) return;
        event.preventDefault();
        this.#openLightbox(trigger);
      },
      { signal }
    );

    // Cerrar: botón close, click en backdrop, Esc.
    const { lightboxClose } = this.refs;
    lightboxClose?.addEventListener('click', () => this.#closeLightbox(), { signal });

    lightbox.addEventListener(
      'click',
      (event) => {
        // Click directo sobre el <dialog>/backdrop (no sobre su contenido) cierra.
        if (event.target === lightbox) this.#closeLightbox();
      },
      { signal }
    );

    lightbox.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.#closeLightbox();
        } else if (event.key === 'Tab') {
          this.#trapFocus(event);
        }
      },
      { signal }
    );
  }

  /** @param {HTMLElement} trigger */
  #openLightbox(trigger) {
    const { lightbox, lightboxStage } = this.refs;
    if (!(lightbox instanceof HTMLElement) || !(lightboxStage instanceof HTMLElement)) return;

    const tile = trigger.closest('[data-ms-ugc-tile]');
    const sourceMedia = tile?.querySelector('[data-ms-ugc-media]');
    if (!sourceMedia) return;

    this.#lastFocused = /** @type {HTMLElement} */ (document.activeElement);

    // Clonar la media (imagen o video) para mostrarla ampliada.
    lightboxStage.replaceChildren();
    // Reset del ratio (un tile previo pudo dejarlo en horizontal).
    lightboxStage.style.aspectRatio = '';
    const clone = /** @type {HTMLElement} */ (sourceMedia.cloneNode(true));
    // En el lightbox el video debe poder reproducirse a tamaño completo con controles.
    const video = clone.matches('video') ? clone : clone.querySelector('video');
    if (video instanceof HTMLVideoElement) {
      video.muted = false;
      video.controls = true;
      video.removeAttribute('tabindex');
      // El tile usa preload="none" (ahorro en el grid); en el modal queremos
      // carga inmediata — si no, el clon puede quedarse negro sin datos.
      video.setAttribute('preload', 'auto');
      // El marco asume 9:16, pero los testimonios reales pueden ser horizontales:
      // al cargar metadata se ajusta al ratio real (adiós franjas negras).
      video.addEventListener(
        'loadedmetadata',
        () => {
          if (video.videoWidth && video.videoHeight) {
            lightboxStage.style.aspectRatio = `${video.videoWidth} / ${video.videoHeight}`;
          }
        },
        { once: true }
      );
    }
    // Imágenes con lazy → forzar carga inmediata dentro del lightbox.
    clone.querySelectorAll('img[loading="lazy"]').forEach((img) => img.setAttribute('loading', 'eager'));
    lightboxStage.append(clone);

    // Mostrar: usar <dialog>.showModal si está disponible (backdrop + inerte nativo).
    if (typeof (/** @type {any} */ (lightbox).showModal) === 'function' && lightbox instanceof HTMLDialogElement) {
      if (!lightbox.open) lightbox.showModal();
    } else {
      lightbox.setAttribute('open', '');
      lightbox.removeAttribute('aria-hidden');
      document.documentElement.style.setProperty('overflow', 'hidden');
    }

    // Foco al cierre (primer elemento accionable del modal).
    const { lightboxClose } = this.refs;
    if (lightboxClose instanceof HTMLElement) {
      lightboxClose.focus();
    } else {
      lightbox.focus();
    }

    if (video instanceof HTMLVideoElement) this.#playLightboxVideo(video);
  }

  /**
   * Reproduce el video clonado del lightbox. Cadena a prueba de móviles:
   * 1) load() explícito — el clon hereda preload="none" y sin esto algunos
   *    navegadores no piden datos (pantalla negra eterna).
   * 2) Intenta CON sonido (viene de un tap = gesto válido).
   * 3) Si la política de autoplay lo bloquea (iOS/Chrome con play indirecto),
   *    reintenta en MUTE para que el video SIEMPRE arranque — el visitante
   *    sube el volumen desde los controles nativos. Antes de este fallback el
   *    catch solo tragaba el error y el modal quedaba como caja negra.
   * @param {HTMLVideoElement} video
   */
  #playLightboxVideo(video) {
    video.load();
    video.muted = false;
    const promise = video.play();
    if (promise && typeof promise.catch === 'function') {
      promise.catch(() => {
        video.muted = true;
        const retry = video.play();
        if (retry && typeof retry.catch === 'function') retry.catch(() => {});
      });
    }
  }

  #closeLightbox() {
    const { lightbox, lightboxStage } = this.refs;
    if (!(lightbox instanceof HTMLElement)) return;

    // Pausar/limpiar el video clonado para liberar recursos.
    const video = lightboxStage?.querySelector('video');
    if (video instanceof HTMLVideoElement) video.pause();
    lightboxStage?.replaceChildren();

    if (lightbox instanceof HTMLDialogElement && lightbox.open) {
      lightbox.close();
    } else {
      lightbox.removeAttribute('open');
      lightbox.setAttribute('aria-hidden', 'true');
    }
    document.documentElement.style.removeProperty('overflow');

    // Devolver el foco al disparador original.
    this.#lastFocused?.focus?.();
    this.#lastFocused = null;
  }

  /**
   * Focus trap manual (respaldo cuando no hay showModal nativo, p. ej. en
   * navegadores sin <dialog> o cuando el modal se abre vía atributo open).
   * @param {KeyboardEvent} event
   */
  #trapFocus(event) {
    const { lightbox } = this.refs;
    if (!(lightbox instanceof HTMLElement)) return;
    // Con <dialog open> nativo el navegador ya inertiza el resto; solo reforzamos
    // el ciclo de Tab dentro del modal cuando lo gestionamos por atributo.
    if (lightbox instanceof HTMLDialogElement && lightbox.open) return;

    const focusables = lightbox.querySelectorAll(
      'a[href], button:not([disabled]), video[controls], [tabindex]:not([tabindex="-1"])'
    );
    if (focusables.length === 0) return;
    const first = /** @type {HTMLElement} */ (focusables[0]);
    const last = /** @type {HTMLElement} */ (focusables[focusables.length - 1]);
    const active = document.activeElement;

    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

if (!customElements.get('ms-ugc-component')) {
  customElements.define('ms-ugc-component', MsUgcComponent);
}
